import { ENUM_LABELS, type DayCloseFigures } from '#shared/types';
import { formatMoney } from '#client/lib/format';

/**
 * What the drawer should hold, and why.
 *
 * Shared by the Day Close screen and the sign-out dialog, so the figures a
 * receptionist confirms at the door read exactly as the admin later sees them.
 */
export function DrawerFigures({ figures }: { figures: DayCloseFigures }) {
  return (
    <dl className="c-summary">
      <SummaryItem label="Billed" value={formatMoney(figures.billed)} />
      {/* Only when there is some. `Billed` includes collected tax, and an
          operator counting the drawer has to be able to explain the gap — but a
          row reading "Rs 0" on every untaxed day is noise. */}
      {figures.tax_collected > 0 && (
        <SummaryItem label="of which sales tax" value={formatMoney(figures.tax_collected)} />
      )}
      <SummaryItem label="Collected (by date taken)" value={formatMoney(figures.collected)} />
      {/* The drawer is counted by method: the cash in the till, the card slips
          and the transfers are three different piles. One method is the
          total again, so it earns no line of its own. */}
      {figures.collected_by_method.length > 1 &&
        figures.collected_by_method.map((row) => (
          <SummaryItem
            key={row.method ?? 'unrecorded'}
            label={`— ${row.method ? ENUM_LABELS.paymentMethod[row.method] : 'Method not recorded'}`}
            value={formatMoney(row.amount)}
          />
        ))}
      {/* Why collected can exceed billed: a balance paid today on an older visit. */}
      {figures.collected_on_earlier_visits !== 0 && (
        <SummaryItem
          label="of which on earlier visits"
          value={formatMoney(figures.collected_on_earlier_visits)}
        />
      )}
      <SummaryItem
        label="Outstanding on these visits"
        value={formatMoney(figures.outstanding)}
        tone={figures.outstanding > 0 ? 'due' : undefined}
      />
    </dl>
  );
}

export function SummaryItem({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'due' | 'watch' | undefined;
}) {
  return (
    <div className="c-summary__row">
      <dt>{label}</dt>
      <dd className={tone ? `c-money--${tone}` : undefined}>{value}</dd>
    </div>
  );
}
